import { memo, Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useRouterState } from '@tanstack/react-router'
import { MdKeyboardArrowDown, MdOutlineViewSidebar } from 'react-icons/md'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarHeaderLogo,
  SidebarMenu,
  SidebarMenuButton,
  SidebarTrigger,
  Text,
} from '@asus-common/fe-ui-shadcn'

import { env } from '@/shared/config/env'
import { useSession } from '@/shared/auth/useSession'
import { useIsMobile } from '@/shared/hooks/useIsMobile'

import asusLogo from '@/assets/asus-logo.svg'
import asusLogoSmall from '@/assets/asus-logo-small.svg'

import { FOOTER_NAV_ITEMS, NAV_GROUPS, PUBLIC_ROUTES } from './nav-items'
import type { NavItem } from './nav-items'
import { useGroupState } from './useGroupState'
import { useSidebarState } from './useSidebarState'

const APP_VERSION = '1.0.0'
const TEXT_MUTED = '#6b7280'

export const NavigationSidebar = memo(function NavigationSidebar() {
  const { t } = useTranslation('navigation')
  const { isLoggedIn } = useSession()
  const isMobile = useIsMobile()
  const { isOpen, toggle } = useSidebarState(isMobile)

  const isOnPublicRoute = useRouterState({
    select: state => PUBLIC_ROUTES.has(state.location.pathname),
  })

  const showUnready = env.showUnreadyFeatures
  const visibleGroups = useMemo(
    () =>
      NAV_GROUPS.map(group => ({
        ...group,
        items: group.items.filter(item => item.ready || showUnready),
      })).filter(group => group.items.length > 0),
    [showUnready],
  )
  const visibleFooterItems = useMemo(
    () => FOOTER_NAV_ITEMS.filter(item => item.ready || showUnready),
    [showUnready],
  )

  if (!isLoggedIn) return null
  if (isOnPublicRoute) return null

  return (
    <Sidebar
      variant="sidebar"
      collapsible="icon"
      state={isOpen ? 'expanded' : 'collapsed'}
      expandedWidth="250px"
      collapsedWidth="64px"
      aria-label={t('main_menu')}
      className="[&_[data-active=true]]:before:hidden [&_[data-testid=sidebar-container]]:!border-r-0 [&_[data-testid=sidebar-container]]:shadow-[var(--shadow-floating-container)]"
    >
      <SidebarHeader
        autoHeight
        className="flex-row items-center justify-between px-5 pt-5 pb-0 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:gap-2 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:pt-4 group-data-[collapsible=icon]:pb-2"
      >
        <SidebarHeaderLogo
          legacyAbsolute={false}
          logoSrc={asusLogo}
          smallLogoSrc={asusLogoSmall}
          logoAlt="ASUS"
          smallLogoAlt="ASUS"
          logoClassName="w-25 h-7 object-contain"
          smallLogoClassName="w-12 h-12 object-contain"
        />
        <SidebarTrigger
          label={t(isOpen ? 'close_menu' : 'open_menu')}
          isMobile={false}
          onClick={toggle}
          size="lg"
          className="text-foreground hover:text-foreground hover:bg-secondary-light mt-0 group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:justify-start group-data-[collapsible=icon]:pl-4"
        >
          <MdOutlineViewSidebar size={18} />
        </SidebarTrigger>
      </SidebarHeader>

      <SidebarContent gap="4px" className="!mt-1 px-0">
        {visibleGroups.map(group => (
          <Fragment key={group.id}>
            <NavGroup
              groupId={group.id}
              labelKey={group.label}
              items={group.items}
              isCollapsed={!isOpen}
            />
          </Fragment>
        ))}
      </SidebarContent>

      <SidebarFooter
        autoHeight
        className="group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-0"
      >
        <SidebarMenu className="group-data-[collapsible=icon]:w-full">
          {visibleFooterItems.map(item => (
            <NavItemRow key={item.to} item={item} isCollapsed={!isOpen} />
          ))}
        </SidebarMenu>
        {isOpen ? (
          <div className="mt-5 px-3 leading-tight">
            <Text fontSize="xs" variant="tertiary">
              {t('version', { version: APP_VERSION })}
            </Text>
            <div className="mt-2">
              <Text fontSize="xs" variant="tertiary">
                {t('copyright_line1', { year: new Date().getFullYear() })}
              </Text>
              <Text fontSize="xs" variant="tertiary">
                {t('copyright_line2')}
              </Text>
            </div>
          </div>
        ) : (
          <div
            className="mt-3 flex flex-col items-center text-center leading-tight"
            style={{ color: TEXT_MUTED }}
          >
            <Text fontSize="xs" variant="tertiary">
              {t('version_short', { version: APP_VERSION })}
            </Text>
            <Text fontSize="xs" variant="tertiary" className="mt-0.5">
              {'© ' + new Date().getFullYear()}
            </Text>
            <Text fontSize="xs" variant="tertiary" className="mt-0.5">
              ASUS
            </Text>
            <Text fontSize="xs" variant="tertiary">
              {t('company_inc')}
            </Text>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  )
})

interface NavGroupProps {
  groupId: string
  labelKey: string
  items: ReadonlyArray<NavItem>
  isCollapsed: boolean
}

const NavGroup = memo(function NavGroup({
  groupId,
  labelKey,
  items,
  isCollapsed,
}: NavGroupProps) {
  const { t } = useTranslation('navigation')
  const { isOpen: groupOpen, toggle } = useGroupState(groupId)
  const label = t(labelKey)
  const showItems = isCollapsed || groupOpen

  return (
    <SidebarGroup gap="4px">
      {!isCollapsed && (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={groupOpen}
          aria-label={
            label + ' -- ' + t(groupOpen ? 'collapse_group' : 'expand_group')
          }
          className="flex w-full items-center justify-between px-3 py-1.5 hover:opacity-80"
          style={{ color: TEXT_MUTED }}
        >
          <Text fontSize="xs" fontWeight="medium" variant="tertiary">
            {label}
          </Text>
          <MdKeyboardArrowDown
            size={14}
            style={{
              transform: groupOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 200ms ease',
            }}
            aria-hidden="true"
          />
        </button>
      )}
      {showItems && (
        <SidebarMenu>
          {items.map(item => (
            <NavItemRow key={item.to} item={item} isCollapsed={isCollapsed} />
          ))}
        </SidebarMenu>
      )}
    </SidebarGroup>
  )
})

interface NavItemRowProps {
  item: NavItem
  isCollapsed: boolean
}

const NavItemRow = memo(function NavItemRow({
  item,
  isCollapsed,
}: NavItemRowProps) {
  const { t } = useTranslation('navigation')
  const label = t(item.label)
  const Icon = item.icon

  const isActive = useRouterState({
    select: state =>
      state.location.pathname === item.to ||
      state.location.pathname.startsWith(item.to + '/'),
  })

  return (
    <Link
      to={item.to}
      aria-label={label}
      aria-current={isActive ? 'page' : undefined}
      className="text-foreground block"
    >
      <SidebarMenuButton
        size="lg"
        activeStripeStyle="full"
        isActive={isActive}
        showTooltip={isCollapsed}
        tooltip={label}
        className="rounded-none group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:justify-start group-data-[collapsible=icon]:pr-0 group-data-[collapsible=icon]:pl-4 hover:bg-[var(--secondary-light)] focus-visible:ring-0 focus-visible:ring-offset-0 data-[active=true]:bg-[var(--secondary-light)] data-[active=true]:shadow-[inset_3px_0_0_0_var(--primary)]"
      >
        <span
          className="relative inline-flex items-center justify-center"
          style={{ width: '20px', height: '20px', flex: '0 0 20px' }}
        >
          <Icon size={18} />
          {isCollapsed && typeof item.badge === 'number' && item.badge > 0 && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                width: '7px',
                height: '7px',
                borderRadius: '999px',
                backgroundColor: '#e11d48',
              }}
            />
          )}
        </span>
        {!isCollapsed && (
          <span
            className="text-md ml-3 truncate font-normal"
            style={{ lineHeight: 1.2 }}
          >
            {label}
          </span>
        )}
        {!isCollapsed && typeof item.badge === 'number' && item.badge > 0 && (
          <span
            aria-label={`${item.badge} ${label}`}
            className="ml-2 inline-flex items-center justify-center rounded-full"
            style={{
              backgroundColor: '#e11d48',
              color: '#fff',
              fontSize: '10px',
              lineHeight: 1,
              fontWeight: 600,
              minWidth: '16px',
              height: '16px',
              padding: '0 4px',
            }}
          >
            {item.badge}
          </span>
        )}
      </SidebarMenuButton>
    </Link>
  )
})
